// vendor libs
import * as Cookie from 'app/packages/cookie-utils';
import experiments from 'app/common/lib/experiments';
import Logging from 'app/common/lib/Logging';
import logStats from 'app/common/lib/logStats';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import user from 'app/common/lib/User';

/**
 * This file is controlling the registration the service worker,
 * sync with our server to associate the browser to user.
 * Service worker is a new thing starting from Chrome 42.0
 * It could run in the chrome background and without any page opened

 * We use service worker to send push notification to the browser.
 */

const webPushManagerLog = Logging.getLogger('WebPushManager');

/**
 * @constructor
 */
const WebPushManager = function() {};

Object.assign(
  WebPushManager.prototype,
  /** @lends {WebPushManager.prototype} */ {
    /**
     * sync with server about the registration id, which is the id of the browser for push notification
     *
     * @param {GCMSubscription} subscription
     */
    sendSubscriptionToServer(subscription, complete, category_name, sourceLandingPage) {
      const unauth_web_push_group =
        experiments.getInstance().v2GetGroup('unauth_desktop_push_notification') || '';
      webPushManagerLog.debug('Calling: sendSubscriptionToServer');
      Cookie.set('cm_sub', 'allowed', 365 * 24, '/');
      if (complete) {
        complete();
      }
      if (!user || !user.isAuthenticated()) {
        logStats.increment('unauth.web.notifications.allowed', 1, {
          unauth_web_push_group,
          category_name,
        });
      } else {
        logStats.increment('auth.web.notifications.allowed', 1, {
          sourceLandingPage,
        });
      }
      let subscriptionId = subscription.subscriptionId;
      if (!subscriptionId) {
        const endpoint_array = subscription.endpoint.split('/');
        subscriptionId = endpoint_array[endpoint_array.length - 1];
      }
      if (user && user.isAuthenticated()) {
        // For authenticated user, check if we've already stored the subcription id in db
        // If not, call resource to store it in db
        const resource = ResourceFactory.create('WebPushDeviceResource', {});
        return resource.callGet({ showError: false }).then((resourceResponse) => {
          const webPushDevices = resourceResponse.resource_response.data;
          if (subscriptionId in webPushDevices) {
            webPushManagerLog.debug('subscription id already exists');
          } else {
            const options = {
              registration_id: subscriptionId,
              subscription_endpoint: subscription.endpoint,
            };
            const webPushDeviceResource = ResourceFactory.create('WebPushDeviceResource', options);
            return webPushDeviceResource.callCreate();
          }
          return undefined;
        });
      } else {
        // For unauth user, callUpdate() backend will handle the case
        // that subscription id already exists for either auth or unauth user
        // It will block adding subscriptipon id to db if already exits
        if (category_name === null || category_name === '' || category_name === undefined) {
          return undefined;
        }

        const unauthOptions = {
          device_id: subscriptionId,
          cat_name: category_name.toUpperCase(),
        };
        const unauthWebPushDeviceResource = ResourceFactory.create(
          'UnauthWebPushDeviceResource',
          unauthOptions,
        );
        return unauthWebPushDeviceResource.callUpdate();
      }
    },

    subscribe(complete, dismiss, category_name, sourceLandingPage) {
      const unauth_web_push_group =
        experiments.getInstance().v2GetGroup('unauth_desktop_push_notification') || '';
      if (!user || !user.isAuthenticated()) {
        logStats.increment('unauth.web.notifications.viewed', 1, {
          unauth_web_push_group,
          category_name,
        });
      } else {
        logStats.increment('auth.web.notifications.viewed', 1, {
          sourceLandingPage,
        });
      }
      return navigator.serviceWorker.ready.then((serviceWorkerRegistration) => {
        serviceWorkerRegistration.pushManager.subscribe({ userVisibleOnly: true }).then(
          (subscription) =>
            this.sendSubscriptionToServer(subscription, complete, category_name, sourceLandingPage),
          (err) => {
            if (Notification.permission === 'denied') {
              Cookie.set('cm_sub', 'denied', 365 * 24, '/');
              if (complete) {
                complete();
              }
              webPushManagerLog.debug('subscribe: Notification.permission === denied');
              if (!user || !user.isAuthenticated()) {
                logStats.increment('unauth.web.notifications.denied', 1, {
                  unauth_web_push_group,
                  category_name,
                });
              } else {
                logStats.increment('auth.web.notifications.denied', 1, {
                  sourceLandingPage,
                });
              }
            } else {
              Cookie.set('cm_sub', 'dismissed', 365 * 24, '/');
              if (dismiss) {
                dismiss();
              }
              webPushManagerLog.debug('subscribe: exception');
              if (!user || !user.isAuthenticated()) {
                logStats.increment('unauth.web.notifications.dismissed', 1, {
                  unauth_web_push_group,
                  category_name,
                });
              } else {
                logStats.increment('auth.web.notifications.dismissed', 1, {
                  sourceLandingPage,
                });
              }
            }
          },
        );
      });
    },

    // Once the service worker is registered set the initial state
    initializeState() {
      // check user login state
      if (user.isLimitedLogin()) {
        webPushManagerLog.debug('User is limited login');
        Cookie.set('cm_sub', 'denied', 365 * 24, '/');
        return undefined;
      }

      const unauth_web_push_group =
        experiments.getInstance().v2GetGroup('unauth_desktop_push_notification') || '';
      if (!user.isAuthenticated() && !unauth_web_push_group.startsWith('enabled')) {
        webPushManagerLog.debug('User not authenticated');
        Cookie.set('cm_sub', 'denied', 365 * 24, '/');
        return undefined;
      }

      // Are Notifications supported in the service worker?
      if (!('showNotification' in ServiceWorkerRegistration.prototype)) {
        webPushManagerLog.debug("Service doesn't support notification");
        Cookie.set('cm_sub', 'denied', 365 * 24, '/');
        return undefined;
      }
      // Check the current Notification permission.
      // If its denied, it's a permanent block until the
      // user changes the permission
      if (Notification.permission === 'denied') {
        webPushManagerLog.debug('User denied notification from Pinterest');
        Cookie.set('cm_sub', 'denied', 365 * 24, '/');
        return undefined;
      }
      // Check if push messaging is supported
      if (!('PushManager' in window)) {
        webPushManagerLog.debug('Push messaging is not supported');
        Cookie.set('cm_sub', 'denied', 365 * 24, '/');
        return undefined;
      }

      return navigator.serviceWorker.ready
        .then((serviceWorkerRegistration) =>
          serviceWorkerRegistration.pushManager.getSubscription(),
        )
        .then((subscription) => {
          if (!subscription) {
            Cookie.set('cm_sub', 'none', 365 * 24, '/');
            throw new Error();
          } else {
            return this.sendSubscriptionToServer(subscription);
          }
        })
        .catch((error) => {
          webPushManagerLog.debug('initialiseState: exception: ', error);
        });
    },
  },
);

WebPushManager.instance = function() {
  if (!WebPushManager._instance) {
    WebPushManager._instance = new WebPushManager();
  }

  return WebPushManager._instance;
};

if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(() => {
        WebPushManager.instance().initializeState();
      });
    } else {
      webPushManagerLog.debug('service worker is not supported');
    }
  });
}

export default WebPushManager;
